"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Command } from "cmdk";

type Action =
  | { type: "anchor"; href: string }
  | { type: "external"; href: string }
  | { type: "route"; href: string }
  | { type: "top" };

type Item = {
  label: string;
  mark: string;
  action: Action;
  keywords?: string[];
  external?: boolean;
};

const groups: { heading: string; items: Item[] }[] = [
  {
    heading: "Navigation",
    items: [
      { label: "Projekte", mark: "→", action: { type: "anchor", href: "#projects" }, keywords: ["produkte", "projects"] },
      { label: "KI & Pipeline", mark: "→", action: { type: "anchor", href: "#ai" }, keywords: ["ai", "aiko", "ki"] },
      { label: "Stack", mark: "→", action: { type: "anchor", href: "#stack" }, keywords: ["technologie", "tech"] },
      { label: "Verlauf", mark: "→", action: { type: "anchor", href: "#timeline" }, keywords: ["timeline", "changelog", "history"] },
      { label: "Kontakt", mark: "→", action: { type: "anchor", href: "#contact" }, keywords: ["contact", "schreiben", "mail"] },
    ],
  },
  {
    heading: "Projekte",
    items: [
      { label: "Aiko", mark: "Ai", action: { type: "anchor", href: "#projects" }, keywords: ["ki", "agenten"] },
      { label: "Logsy Bot", mark: "Lo", action: { type: "anchor", href: "#projects" }, keywords: ["discord", "bot"] },
      { label: "AzubiLern", mark: "Az", external: true, action: { type: "external", href: "https://azubilern.de" }, keywords: ["lernen", "kurse"] },
      { label: "Icon RP", mark: "Ic", external: true, action: { type: "external", href: "https://iconrp.de" }, keywords: ["fivem", "roleplay"] },
    ],
  },
  {
    heading: "Ressourcen",
    items: [
      { label: "GitHub", mark: "Gh", external: true, action: { type: "external", href: "https://github.com/logsy" }, keywords: ["code", "repo"] },
      { label: "E-Mail schreiben", mark: "@", action: { type: "external", href: "mailto:hello@logsy.de" }, keywords: ["kontakt", "mail"] },
      { label: "Impressum", mark: "Im", action: { type: "route", href: "/impressum" }, keywords: ["rechtliches", "legal", "ddg", "anbieter"] },
      { label: "Datenschutz", mark: "Ds", action: { type: "route", href: "/datenschutz" }, keywords: ["dsgvo", "privacy", "daten"] },
      { label: "Nutzungsbedingungen", mark: "Nb", action: { type: "route", href: "/nutzungsbedingungen" }, keywords: ["agb", "terms", "tos", "regeln"] },
    ],
  },
  {
    heading: "Aktionen",
    items: [{ label: "Nach oben", mark: "↑", action: { type: "top" }, keywords: ["top", "anfang"] }],
  },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("logsy:command", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("logsy:command", onOpen);
    };
  }, []);

  const reduce = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const go = (action: Action) => {
    setOpen(false);
    // Kurz warten, bis der Dialog geschlossen ist, bevor gescrollt/navigiert wird.
    setTimeout(() => {
      switch (action.type) {
        case "anchor": {
          const el = document.querySelector(action.href);
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 70;
            window.scrollTo({ top, behavior: reduce() ? "auto" : "smooth" });
          }
          break;
        }
        case "top":
          window.scrollTo({ top: 0, behavior: reduce() ? "auto" : "smooth" });
          break;
        case "external":
          if (action.href.startsWith("mailto:")) window.location.href = action.href;
          else window.open(action.href, "_blank", "noopener,noreferrer");
          break;
        case "route":
          router.push(action.href);
          break;
      }
    }, 10);
  };

  return (
    <Command.Dialog open={open} onOpenChange={setOpen} label="Befehle & Navigation">
      <Command.Input placeholder="Suchen oder Befehl eingeben…" />
      <Command.List>
        <Command.Empty>Keine Treffer.</Command.Empty>
        {groups.map((group) => (
          <Command.Group key={group.heading} heading={group.heading}>
            {group.items.map((item) => (
              <Command.Item
                key={item.label}
                value={item.label}
                keywords={item.keywords}
                onSelect={() => go(item.action)}
              >
                <span className="cmdk-mark">{item.mark}</span>
                <span className="cmdk-label">{item.label}</span>
                {item.external && <span className="cmdk-ext">↗</span>}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
      <div className="cmdk-foot">
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> navigieren
        </span>
        <span>
          <kbd>↵</kbd> öffnen
        </span>
        <span>
          <kbd>esc</kbd> schließen
        </span>
      </div>
    </Command.Dialog>
  );
}
